/**
 * CrewCup promo pop-up.
 *
 * Native <dialog> + showModal(), so the focus trap, Escape handling, page
 * inertness, ::backdrop and top-layer promotion all come from the platform
 * rather than from here. Same approach as crewcup-faq-chips.js.
 *
 * Four things the platform does NOT give us, which is most of what is below:
 *
 *  1. SCROLL LOCK. showModal() does not lock this theme on desktop. At >=990px
 *     the scroll container is .page-wrapper, not the document (base.css:32-59),
 *     so the page happily scrolls behind an open modal. We have to go through
 *     the theme's own lockScroll/unlockScroll, which set html[scroll-lock] and
 *     keep an owner Set — using them rather than setAttribute is what stops us
 *     clobbering the cart drawer's lock when both are open.
 *
 *  2. FOCUS RESTORATION. Native returns focus to whatever was focused when
 *     showModal() was called. For a TIMER-opened dialog that is <body>, so on
 *     close a keyboard user is dumped at the top of the tab order with no
 *     announcement. crewcup-faq-chips.js has no equivalent code because a real
 *     button opens those.
 *
 *  3. DISMISSAL MEMORY, and doing it without throwing where storage is blocked.
 *
 *  4. NOT OPENING ON TOP OF SOMETHING ELSE.
 */
if (!window.__crewcupPromoPopup) {
  window.__crewcupPromoPopup = true;

  const KEY_PREFIX = 'crewcup-promo-popup:';

  /* ---------------------------------------------------------------- storage */

  /**
   * Storage may throw rather than merely return null: private windows, blocked
   * cookies, and most relevantly the theme editor, whose preview is a
   * cross-site iframe inside admin.shopify.com. Safari's cross-site tracking
   * prevention makes that the likeliest failure in practice.
   *
   * Fail OPEN throughout — if we cannot tell whether it has been seen, show it.
   * Failing closed would silently kill the promo for a whole cohort of
   * visitors, which is a much worse outcome than one extra impression.
   */
  let memoryFallback = false;
  let shownInThisPageLoad = false;

  const probe = (store) => {
    try {
      const k = '__ccp_probe';
      store.setItem(k, '1');
      store.removeItem(k);
      return true;
    } catch {
      return false;
    }
  };

  const pick = (frequency) => {
    try {
      const store = frequency === 'days' ? window.localStorage : window.sessionStorage;
      if (!store || !probe(store)) return null;
      return store;
    } catch {
      return null;
    }
  };

  const readFlag = (store, key) => {
    if (!store) return null;
    try {
      return store.getItem(key);
    } catch {
      return null;
    }
  };

  const writeFlag = (store, key, value) => {
    if (!store) {
      // No storage at all. An in-memory flag at least gives once-per-page-load
      // instead of once-per-navigation, which beats nagging on every scroll.
      memoryFallback = true;
      return;
    }
    try {
      store.setItem(key, value);
    } catch {
      memoryFallback = true;
    }
  };

  /** True when this campaign has already been shown and should stay hidden. */
  const alreadySeen = (dialog) => {
    const frequency = dialog.dataset.ccpFrequency ?? 'session';
    if (frequency === 'always') return false;
    if (memoryFallback && shownInThisPageLoad) return true;

    const key = KEY_PREFIX + (dialog.dataset.ccpCampaign ?? 'default');
    const store = pick(frequency);
    const raw = readFlag(store, key);
    if (!raw) return false;

    if (frequency === 'session') return true;

    const days = Number.parseInt(dialog.dataset.ccpDays ?? '7', 10);
    const then = Number.parseInt(raw, 10);
    if (!Number.isFinite(then)) return false;
    return Date.now() - then < days * 86400000;
  };

  const recordShown = (dialog) => {
    shownInThisPageLoad = true;
    const frequency = dialog.dataset.ccpFrequency ?? 'session';
    if (frequency === 'always') return;
    const key = KEY_PREFIX + (dialog.dataset.ccpCampaign ?? 'default');
    writeFlag(pick(frequency), key, String(Date.now()));
  };

  /* ------------------------------------------------------- scroll lock glue */

  /**
   * Imported lazily so a storage-blocked or slow utilities.js never delays the
   * decision to open. utilities.js is already modulepreloaded by
   * snippets/scripts.liquid, so this resolves from cache in practice.
   */
  const scrollLock = import('@theme/utilities').catch(() => null);

  const lock = async (owner) => {
    const mod = await scrollLock;
    mod?.lockScroll?.(owner);
  };

  const unlock = async (owner) => {
    const mod = await scrollLock;
    mod?.unlockScroll?.(owner);
  };

  /* -------------------------------------------------------- open / close */

  let lastFocused = null;

  /**
   * Is this element actually on screen? offsetParent is NOT a visibility test —
   * it is non-null for anything that is not display:none, including content
   * inside a closed, translated-off-screen drawer. checkVisibility() accounts
   * for opacity and visibility as well; the rect check is the fallback.
   */
  const isReallyVisible = (el) => {
    if (!(el instanceof HTMLElement)) return false;
    try {
      if (typeof el.checkVisibility === 'function') {
        return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
      }
    } catch (e) {
      /* fall through to the rect test */
    }
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  };

  /**
   * Reasons not to open, checked immediately before showModal() rather than when
   * the timer is scheduled — all of this can change during the delay.
   */
  const somethingElseIsOpen = () => {
    // Any other native dialog: search modal, quick-add, FAQ chips, popup-link.
    const openDialog = document.querySelector('dialog[open]');
    if (openDialog) return true;

    // Cart / theme drawers. snippets/theme-drawer.liquid can restore a drawer
    // open at first paint at >=990px from sessionStorage, so one can legitimately
    // be open by the time our timer fires.
    if (document.querySelector('theme-drawer[open]')) return true;

    // Rebuy's add-to-cart cross-sell popup, and ONLY that.
    //
    // This check previously also matched [data-rebuy-component="cross-sell-widget"]
    // and tested offsetParent !== null. That selector matches Smart Cart's IN-CART
    // cross-sell, which lives inside the closed .rebuy-cart__flyout — and
    // offsetParent is non-null for anything not display:none. So once Rebuy's
    // script had loaded, this returned true forever and the pop-up never opened.
    // It only appeared to work at all because a 1s delay beat Rebuy loading;
    // raising the delay to 10s exposed it.
    const rebuyPopup = document.querySelector('.rebuy-widget.widget-display-popup');
    if (isReallyVisible(rebuyPopup)) return true;

    return false;
  };

  const open = (dialog) => {
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    if (somethingElseIsOpen()) return;

    lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.showModal();
    lock(dialog);
    // Recorded on OPEN, not on dismiss: a shopper who reloads or navigates away
    // with it open has still seen it, and should not see it again.
    recordShown(dialog);
  };

  /**
   * Native restores focus to whatever was focused before showModal(). For a
   * timer-opened dialog that is nothing, so send focus to main content instead.
   * tabindex is added from here and removed on blur so we never have to edit
   * layout/theme.liquid to leave a permanent tabindex on #MainContent.
   */
  const restoreFocus = () => {
    if (lastFocused && lastFocused.isConnected && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
      lastFocused = null;
      return;
    }
    lastFocused = null;

    const main = document.getElementById('MainContent');
    if (!main) return;
    main.setAttribute('tabindex', '-1');
    main.focus({ preventScroll: true });
    main.addEventListener('blur', () => main.removeAttribute('tabindex'), { once: true });
  };

  /* --------------------------------------------------------------- wiring */

  const wire = (dialog) => {
    if (dialog.dataset.ccpWired === 'true') return;
    dialog.dataset.ccpWired = 'true';

    dialog.addEventListener('close', () => {
      unlock(dialog);
      restoreFocus();
    });

    // A lock must never survive a navigation. Horizon uses view transitions, so
    // the page can be swapped without this module being torn down.
    window.addEventListener('pagehide', () => unlock(dialog));

    dialog.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-ccp-close]')) {
        dialog.close();
        return;
      }

      // The CTA navigates; close first so the lock is released and focus is not
      // left inside a dialog that is about to be discarded.
      if (target.closest('[data-ccp-cta]')) {
        dialog.close();
        return;
      }

      // Backdrop. The backdrop is not its own element, so a click on it lands on
      // the dialog itself; any click inside lands on a descendant. Only correct
      // because the dialog carries no padding of its own — see the section file.
      if (target === dialog) dialog.close();
    });

    const inEditor = dialog.dataset.ccpEditor === 'true';

    if (inEditor) {
      // Editor: driven entirely by selection, never by a timer, and storage is
      // not consulted at all — see the storage comment above.
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target instanceof Element && event.target.contains(dialog) && !dialog.open) {
          dialog.showModal();
        }
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target instanceof Element && event.target.contains(dialog) && dialog.open) {
          dialog.close();
        }
      });
      return;
    }

    if (alreadySeen(dialog)) return;

    const delay = Number.parseInt(dialog.dataset.ccpDelay ?? '1000', 10);
    window.setTimeout(() => open(dialog), Number.isFinite(delay) ? delay : 1000);
  };

  const init = () => {
    document.querySelectorAll('dialog[data-ccp]').forEach((el) => {
      if (el instanceof HTMLDialogElement) wire(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Re-wire after the theme editor re-injects the section.
  document.addEventListener('shopify:section:load', init);

  /**
   * Exposed for QA and for A/B tools that swap copy after load: an offer test
   * that rewrites the text should open the pop-up itself once it is done, rather
   * than letting our timer show variant A and flicker to variant B.
   */
  window.crewcupPromoPopup = {
    open: () => {
      const el = document.querySelector('dialog[data-ccp]');
      if (el instanceof HTMLDialogElement) open(el);
    },
    close: () => {
      const el = document.querySelector('dialog[data-ccp][open]');
      if (el instanceof HTMLDialogElement) el.close();
    },
  };
}
